
import React from 'react';
import { X, FileBarChart, Users, Zap, Clock, TrendingUp, AlertCircle, Activity, HeartPulse, UserCheck, ShieldAlert } from 'lucide-react';
import { useAppContext } from '../contexts/AppContext';
import { HUDButton } from './HUD/HUDPrimitives';

const SimulationReport: React.FC = () => {
  const { state, dispatch } = useAppContext();
  const { isReportOpen, stats, pixelsPerMeter } = state;

  if (!isReportOpen) return null;

  const formatDistance = (px: number) => ((px / pixelsPerMeter) / 1000).toFixed(2); // KM
  const formatPercent = (val: number) => (val * 100).toFixed(1) + '%';

  const StatCard = ({ title, value, icon: Icon, color, subValue }: any) => (
    <div className="bg-slate-900/60 border border-white/5 p-6 rounded-2xl space-y-4">
      <div className="flex justify-between items-start">
        <div className={`p-3 bg-${color}-500/10 rounded-xl border border-${color}-500/20 text-${color}-400`}>
          <Icon size={20} />
        </div>
        <TrendingUp size={16} className="text-slate-700" />
      </div>
      <div>
        <h4 className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">{title}</h4>
        <div className="flex items-baseline gap-2">
          <span className="text-2xl font-mono font-bold text-white tracking-tighter">{value}</span>
          {subValue && <span className="text-[10px] text-slate-500 font-mono">{subValue}</span>}
        </div>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/90 backdrop-blur-xl p-6 animate-in fade-in zoom-in-95 duration-300">
      <div className="w-full max-w-5xl bg-slate-950 border border-white/10 rounded-3xl shadow-[0_0_100px_rgba(0,0,0,0.5)] flex flex-col overflow-hidden max-h-[90vh]">
        
        {/* Header */}
        <div className="p-8 border-b border-white/10 flex justify-between items-center bg-slate-900/50">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-emerald-500/20 rounded-xl border border-emerald-500/40">
              <FileBarChart className="text-emerald-400" size={24} />
            </div>
            <div>
              <h2 className="text-2xl font-rajdhani font-bold text-white tracking-widest uppercase">
                Simuliacijos Ataskaita
              </h2>
              <p className="text-[10px] text-slate-500 font-mono uppercase tracking-wider">Sugeneruota: {new Date().toLocaleTimeString()}</p>
            </div>
          </div>
          <button onClick={() => dispatch({ type: 'SET_REPORT_OPEN', payload: false })} className="p-3 hover:bg-white/5 rounded-full text-slate-400 hover:text-white transition-colors">
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-10 space-y-10 custom-scrollbar">
          
          {/* Main Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <StatCard title="Išgydyta pacientų" value={stats.treatedPatients} icon={UserCheck} color="emerald" subValue="VNT." />
            <StatCard title="Vid. laukimo laikas" value={stats.avgWaitTime.toFixed(1)} icon={Clock} color="amber" subValue="MIN." />
            <StatCard title="Kritiniai įvykiai" value={stats.criticalEvents} icon={ShieldAlert} color="red" subValue="VNT." />
            <StatCard title="Infekcijos perdavimai" value={stats.infections} icon={Activity} color="lime" subValue="VNT." />
          </div>

          {/* Row 2: Logistic Analysis */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-slate-900/40 border border-white/5 p-8 rounded-3xl space-y-6">
               <h3 className="text-xs font-bold text-cyan-400 uppercase tracking-[0.2em] flex items-center gap-2">
                 <Zap size={16}/> Logistinis Krūvis (Distancija)
               </h3>
               <div className="space-y-4">
                  {[
                    { label: 'Gydytojai', val: stats.avgDistanceByRole.doctors, color: 'white' },
                    { label: 'Slaugytojai', val: stats.avgDistanceByRole.nurses, color: 'pink' },
                    { label: 'Pacientai', val: stats.avgDistanceByRole.patients, color: 'cyan' }
                  ].map(item => (
                    <div key={item.label} className="space-y-2">
                       <div className="flex justify-between text-[10px] font-bold uppercase tracking-wider">
                          <span className="text-slate-400">{item.label}</span>
                          <span className="text-white font-mono">{formatDistance(item.val)} KM</span>
                       </div>
                       <div className="h-1.5 bg-black/40 rounded-full overflow-hidden">
                          <div className={`h-full bg-${item.color}-500 opacity-60`} style={{ width: `${Math.min(100, item.val / 50000)}%` }}></div>
                       </div>
                    </div>
                  ))}
               </div>
               <p className="text-[9px] text-slate-600 italic">Distancija matuojama sumuojant visų agentų nuveiktą kelią simuliacijos metu.</p>
            </div>

            <div className="bg-slate-900/40 border border-white/5 p-8 rounded-3xl space-y-6">
               <h3 className="text-xs font-bold text-emerald-400 uppercase tracking-[0.2em] flex items-center gap-2">
                 <HeartPulse size={16}/> Personalo Užimtumas (Utilization)
               </h3>
               <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-black/20 rounded-2xl border border-white/5 text-center">
                     <span className="block text-[8px] text-slate-500 uppercase mb-2">Gydytojų aktyvumas</span>
                     <span className="text-2xl font-mono font-bold text-emerald-400">{formatPercent(stats.staffUtilization.doctors)}</span>
                  </div>
                  <div className="p-4 bg-black/20 rounded-2xl border border-white/5 text-center">
                     <span className="block text-[8px] text-slate-500 uppercase mb-2">Slaugytojų aktyvumas</span>
                     <span className="text-2xl font-mono font-bold text-emerald-400">{formatPercent(stats.staffUtilization.nurses)}</span>
                  </div>
               </div>
               <div className="p-4 bg-red-500/5 border border-red-500/10 rounded-2xl flex items-start gap-3">
                  <AlertCircle size={16} className="text-red-400 shrink-0" />
                  <div className="space-y-1">
                    <span className="text-[10px] font-bold text-red-400 uppercase tracking-widest">Efficiency Alert</span>
                    <p className="text-[9px] text-slate-500 leading-tight">Jei užimtumas viršija 85%, skyriuje formuojasi „butelio kakliukai“ (bottlenecks).</p>
                  </div>
               </div>
            </div>
          </div>

        </div>

        {/* Footer */}
        <div className="p-8 border-t border-white/10 bg-slate-900/80 flex justify-between items-center">
          <div className="text-[10px] font-mono text-slate-600 uppercase tracking-widest">
            SMPS ANALYTICS ENGINE // HASH: {Math.random().toString(16).slice(2, 8).toUpperCase()}
          </div>
          <HUDButton 
            variant="emerald" 
            className="px-10"
            onClick={() => window.print()}
          >
            Spausdinti PDF
          </HUDButton>
        </div>
      </div>
    </div>
  );
};

export default SimulationReport;
